import { extendTheme } from '@chakra-ui/react';

import { getNestedValue } from './getNestedValue';

import type { PaymentElementThemeType } from '../@types';
import type {
  StripeCardNumberElementOptions,
  StripeCardCvcElementOptions,
  StripeCardExpiryElementOptions,
  Appearance,
} from '@stripe/stripe-js';

type DefaultCssType = Record<string, any>;

const transformCss = (
  el: any,
  colors: DefaultCssType,
  sizes: DefaultCssType,
) => {
  const colorValue = getNestedValue(colors, el, null);
  const sizeValue = getNestedValue(sizes, el, null);
  return colorValue || sizeValue || el;
};

const prepareStripeCss = (
  chakraStyle: DefaultCssType,
  colors: DefaultCssType,
  sizes: DefaultCssType,
) => {
  if (!chakraStyle || typeof chakraStyle !== 'object') return {};
  return Object.keys(chakraStyle).reduce((acc: DefaultCssType, el: string) => {
    const node = chakraStyle[el];
    /** @TODO Quickfix, improve. */
    let key = el.startsWith('_') ? el.replace('_', ':') : el;
    key = key === ':placeholder' ? '::placeholder' : key;
    key = key === 'bg' ? 'backgroundColor' : key;
    acc[key] =
      typeof node === 'object' && node !== null
        ? prepareStripeCss(node, colors, sizes)
        : transformCss(chakraStyle[el], colors, sizes);
    return acc;
  }, {});
};

export const generateStripeTheme = (components: any, foundations: any) => {
  const base = prepareStripeCss(
    components.Input.baseStyle.field,
    foundations.colors,
    foundations.fontSizes,
  );
  const accordionButton = prepareStripeCss(
    components.Accordion.baseStyle.button,
    foundations.colors,
    foundations.fontSizes,
  );
  const accordionButtonText = prepareStripeCss(
    components.Heading.variants.h2,
    foundations.colors,
    foundations.fontSizes,
  );
  const accordionRadio = prepareStripeCss(
    components.Radio.baseStyle.control,
    foundations.colors,
    foundations.fontSizes,
  );

  /**
   * Base style for Form component - handles both functions and objects
   */
  const formBaseStyle =
    typeof components?.Form?.baseStyle === 'function'
      ? components.Form.baseStyle({
          isInvalid: false,
          theme: { colors: foundations.colors },
        })?.container
      : components?.Form?.baseStyle || {};

  const peInput = prepareStripeCss(
    formBaseStyle,
    foundations.colors,
    foundations.fontSizes,
  );
  const invalidColor = transformCss(
    // eslint-disable-next-line no-underscore-dangle
    components.FormLabel.baseStyle._invalid.color,
    foundations.colors,
    foundations.fontSizes,
  );
  const baseInput = {
    style: {
      base,
      invalid: {
        iconColor: invalidColor,
        color: invalidColor,
      },
    },
  };
  const cardInput: StripeCardNumberElementOptions = {
    showIcon: true,
    iconStyle: 'default',
    ...baseInput,
  };
  const cvcInput: StripeCardCvcElementOptions = {
    ...baseInput,
  };
  const expiryInput: StripeCardExpiryElementOptions = {
    ...baseInput,
  };

  const paymentElement: Appearance = {
    theme: 'stripe',
    labels: 'floating',
    inputs: 'spaced',
    // disableAnimations: true,
    variables: {
      colorDanger: invalidColor,
      fontFamily: 'Poppins, sans-serif',
    },
    rules: {
      '.AccordionItem': {
        backgroundColor: accordionButton.backgroundColor || '#f8f9fa',
        borderRadius: accordionButton.borderRadius || '16px',
        borderWidth: accordionButton.borderWidth || '1px',
        borderColor: accordionButton.borderColor || '#dde0e4',
        boxShadow: 'none',
        color: accordionButton.color || '#1e1e1e',
        fontSize: accordionButtonText.fontSize || '16px',
        fontWeight: accordionButtonText.fontWeight || '600',
        marginBottom: '0px',
        padding: '16px',
        lineHeight: '24px',
        fontFamily: 'Poppins, sans-serif',
      },
      '.AccordionItem--selected': {
        color: accordionButton.color || '#1e1e1e',
      },
      '.TabIcon': {
        fill: accordionButton.color || '#1e1e1e',
      },
      '.RadioIcon': {
        width: accordionRadio.width || '23px',
      },
      '.RadioIconInner': {
        r: '26',
        fill: accordionRadio.borderColor || '#778295',
      },
      '.RadioIconOuter': {
        strokeWidth: '7',
        stroke: accordionRadio.borderColor || '#778295',
      },
      '.Input': {
        ...peInput,
      },
      // Force all label states to have the same positioning (as if they were focused)
      '.Label': {
        position: 'absolute',
        top: '0',
        left: '12px',
        transform: 'translateY(-50%)',
        backgroundColor: 'var(--colorBackground)',
        padding: '0 4px',
        fontSize: '12px',
        transition: 'none', // Disable all transitions
      },

      // Make sure specific states don't override our positioning
      '.Label--floating': {
        top: '0',
        transform: 'translateY(-50%)',
      },

      '.Label--resting': {
        top: '0',
        transform: 'translateY(-50%)',
      },
    },
  };

  return {
    cardInput,
    cvcInput,
    expiryInput,
    paymentElement,
  };
};

export const generateMainTheme = (theme: PaymentElementThemeType) => {
  const {
    globalStyles: global,
    colorMode: initialColorMode,
    foundations,
    components,
  } = theme;

  const themeDefinitions = {
    styles: {
      global,
    },
    config: {
      initialColorMode,
      useSystemColorMode: false,
    },
    ...foundations,
    components: {
      ...components,
    },
  };

  return extendTheme(themeDefinitions);
};
